<template>
  <div stripe-content-player-body-component ref="playerBody" :style="bodyStyle" class="text-black stripe-content-player-body px-0">
    <div class="handle w-100 cursor-pointer" @click="expandContentBody" v-if="!expanded"></div>
    <div class="controls px-3 d-flex align-items-center">
      <div class="cursor-pointer" @click="expandContentBody">
        <i class="fa-light fa-chevron-up" :class="{'fa-rotate-180': expanded}"></i>
      </div>
      <div v-if="!expanded" class="w-100 text-center cursor-pointer" @click="expandContentBody">
        <span>{{ ft("content_player.article_expand") }}</span>
      </div>
    </div>
    <div class="flickity-carousel" ref="flickity-carousel">
      <div 
        class="flickity-carousel-item"
        ref="slides"
        v-for="(chapter, index) in chapters"
        :key="`chapter-${chapter.$id}`"
      >
        <component
          class="chapter-content"
          :is="chapter.layout"
          :content="chapter"
          :opt-container-content="optContainerContent"
        ></component>
        <div v-if="index<content.chapters.length-1" class="scroll-helper border-top text-primary d-flex align-items-center justify-content-center px-4 pb-4">
          <div class="overlay"></div>
          <i class="fa-light fa-chevron-right pr-2"></i>
          <div class="scroll-helper__label">
            <p class="mb-0 py-3" v-html="ft('content_player.scroll')"></p>
          </div>
        </div>
      </div>
    </div>
    <div ref="progressEl" class="progress bg-white">
      <div class="progress-bar" :style="progressStyle">
        <div :class="{'fadeInOut': showLabel}" class="progress-counter">{{ progressLabel }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { fandomBasicMixin } from 'src/modules/fandom_mixins_module.js';
import Flickity from "flickity";

const ANIMATION_EXPAND = {
  transform: "translateY(-100%)",
  scale: ['0.9', '1'],
  borderRadius: 0,
  easing: 'ease-in-out',
  backgroundColor: '#FFFFFF'
};

const ANIMATION_APPEAR = {
  transform: ['translateY(calc(-1 * var(--handle-height)))'],
  borderRadius: ".5rem .5rem 0 0",
  scale: "0.9",
  easing: 'ease-in-out'
};

export default {
  mixins: [fandomBasicMixin],
  props: {
    expanded: {
      type: Boolean,
      required: true
    },
    lastSlide: {
      type: Boolean,
      required: true
    },
    initialIndex: {
      type: Number,
      default: 0
    }
  },
  data: function() {
    return {
      slider: null,
      showLabel: false,
      animation: null,
      touchStart: null
    };
  },
  mounted() {
    Vue.nextTick(() => {
      this.initCarousel();
      if (this.expanded) {
        this.runAnimation(ANIMATION_EXPAND, 500);
        this.showHideProgressLabel();
      } else {
        this.addTouchListeners();
        this.runAnimation(ANIMATION_APPEAR, 1000, true);
      }
    });
  },
  methods:{
    async runAnimation(keyframes, duration, commitStyles=false) {
      const playerBody = this.$refs.playerBody;
      this.animation = playerBody.animate(keyframes, { duration, fill: "forwards" });
      if (commitStyles) {
        await this.animation.finished;
        playerBody.offsetParent != null && this.animation.commitStyles();
      }
    },
    handleTouchStart(event) {
      this.touchStart = event.touches[0].clientY;
    },
    handleTouchEnd(event) {
      const deltaThreshold = 100;
      const touchDelta = this.touchStart - event.changedTouches[0].clientY;

      if (touchDelta > deltaThreshold) {
        this.expandContentBody();
      }
    },
    addTouchListeners() {
      window.addEventListener('touchstart', this.handleTouchStart);
      window.addEventListener('touchend', this.handleTouchEnd);
    },
    removeTouchListeners() {
      window.removeEventListener('touchstart', this.handleTouchStart);
      window.removeEventListener('touchend', this.handleTouchEnd);
    },
    expandContentBody() {
      this.$emit('expand-content-body', !this.expanded);
      this.resetSlidesTopPosition();
    },
    initCarousel() {
      this.slider = new Flickity(this.$refs["flickity-carousel"], {
        draggable: "> 1",
        contain: true,
        pageDots: false,
        cellAlign: "center",
        prevNextButtons: true,
        initialIndex: this.initialIndex,
        on: {
          change: (_) => {
            this.resetSlidesTopPosition();
          }
        }
      });
    },
    showHideProgressLabel() {
      this.showLabel = true;
      setTimeout(() => {
        this.showLabel = false;
      }, 1000);
    },
    resetSlidesTopPosition() {
      this.$refs.slides.forEach(slide => {
        slide.scrollTop = 0
      });
    }
  },
  computed: {
    chapters() {
      return this.content.chapters.map((c, index) => {
        const chapter = {...c};
        if (c.title.length>0) {
          chapter.title = `${index+1}. ${c.title}`
        }

        return chapter;
      })
    },
    currentIndex() {
      return this.slider?.selectedIndex ?? 0;
    },
    progressStyle() {
      if (!this.slider) {
        return {};
      }

      return {
        width: `${(this.currentIndex+1)/(this.slider.slides.length)*100}%`,
        '--progress-step-width': `${(this.$refs["progressEl"]?.clientWidth / this.slider.slides.length) / 2}px`
      }
    },
    progressLabel() {
      return this.slider?.selectedIndex+1;
    },
    bodyStyle() {
      return {
        'backgroundColor': this.content.label_color ?? 'white'
      }
    }
  },
  watch: {
    expanded(newVal) {
      this.slider?.resize();
      if (newVal) {
        this.removeTouchListeners();
        this.runAnimation(ANIMATION_EXPAND, 500);
        this.showHideProgressLabel();
      } else {
        this.addTouchListeners();
        this.animation.reverse();
      }
    },
    initialIndex() {
      this.slider?.select(this.initialIndex, true, true);
    },
    currentIndex() {
      this.showHideProgressLabel();
    },
    lastSlide(newVal) {
      if (newVal) {
        this.runAnimation({...ANIMATION_APPEAR, transform: "translateY(calc(-1 * var(--handle-height) - 30%))"}, 500, true);
      } else if (!this.expanded) {
        this.runAnimation(ANIMATION_APPEAR, 500, true);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
[stripe-content-player-body-component] {
  --progress-counter-size: 2rem;
  --progress-step-width: 2rem;
  --scroll-helper: 100px;

  position: absolute;
  z-index: calc(var(--player-z-index) + 2);
  height: var(--viewport-height);
  bottom: -100%;
  left: 0;
  right: 0;
  border-radius: .5rem;
  overflow: hidden;
  scale: .9;
  transform-origin: top;
  // FOR BETTER ANIMATION PERFORMANCE
  will-change: transform, border-radius, scale, background-color;

  @include media-breakpoint-up(sm) {
    height: calc(var(--viewport-height) - (2 * var(--box-size)));
  }

  .handle {
    position: absolute;
    left: 0;
    height: var(--viewport-height);
    z-index: 2050;
  }

  .controls {
    position: absolute;
    width: 100%;
    left: 0;
    z-index: 10;
    height: calc(var(--handle-height) + 5px); // scale adaption
  }

  ::v-deep .flickity-prev-next-button {
    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  .flickity-carousel-item {
    width: 100%;
    height: calc(var(--viewport-height) - (2 * var(--box-size)));
    overflow-y: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .chapter-content {
      padding: calc(var(--handle-height) + 1rem) 0 1.5rem 0 !important;
      min-height: calc(100dvh - (2 * var(--box-size)) - var(--scroll-helper));

      ::v-deep {
        [title] {
          font-family: 'VodafoneEx';
        }
      }
    }

    .scroll-helper {
      position: relative;
      height: var(--scroll-helper);

      .overlay {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 80%;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, $white 62.11%);
      }

      &__label {
        font-size: .875rem;
        line-height: 1rem;
      }
    }
  }

  .progress {
    position: absolute;
    width: 100%;
    padding: 0;
    margin: 0;
    bottom: 0;
    height: 4px;
    overflow: visible;

    &-bar {
      transition: width .4s linear;
      overflow: visible;
      position: relative;
    }

    &-counter {
      position: absolute;
      width: var(--progress-counter-size);
      height: var(--progress-counter-size);
      background: $primary;
      top: -2.5rem;
      border-radius: 50%;
      left: calc(100% - (var(--progress-counter-size) / 2) - var(--progress-step-width));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      font-weight: 600;
      opacity: 0;

      &.fadeInOut {
        animation: fadeInOut .8s linear 0s 1 normal;
      }
    }
  }

  @keyframes fadeInOut {
    0% { opacity: 0; }
    40% { opacity: 1; }
    80% { opacity: 1; }
    100% { opacity: 0; }
  }

}
</style>